import { useCallback, useEffect, useRef, useState } from 'react'
import { kanbify } from '../../lib/kanbifyClient'

const MIN_VIEW_SECONDS = 1
const MAX_VIEW_SECONDS = 8 * 60 * 60
const SESSION_KEY = 'kanbify.shim.session'
const VIEW_SESSION_PREFIX = 'kanbify.card-view.elapsed'
const rememberedElapsedSeconds = new Map()

function currentUserId() {
    try {
        const session = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null')
        return session?.user?.id || 'current-user'
    } catch {
        return 'current-user'
    }
}

function elapsedStorageKey(cardId) {
    return `${VIEW_SESSION_PREFIX}:${currentUserId()}:${cardId}`
}

function readRememberedElapsed(cardId) {
    if (!cardId) return 0
    const key = elapsedStorageKey(cardId)
    if (rememberedElapsedSeconds.has(key)) return rememberedElapsedSeconds.get(key)
    try {
        const stored = Math.max(0, Math.min(MAX_VIEW_SECONDS, Number(sessionStorage.getItem(key)) || 0))
        rememberedElapsedSeconds.set(key, stored)
        return stored
    } catch {
        return 0
    }
}

function rememberElapsed(cardId, seconds) {
    if (!cardId) return
    const key = elapsedStorageKey(cardId)
    const safeSeconds = Math.max(0, Math.min(MAX_VIEW_SECONDS, Math.round(seconds || 0)))
    rememberedElapsedSeconds.set(key, safeSeconds)
    try {
        sessionStorage.setItem(key, String(safeSeconds))
    } catch { /* session storage is best-effort */ }
}

function authHeaders() {
    try {
        const session = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null')
        return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}
    } catch {
        return {}
    }
}

export default function useCardViewSession(cardId, viewContext) {
    const sessionRef = useRef(null)
    const persistedRef = useRef(false)
    const [elapsedSeconds, setElapsedSeconds] = useState(0)

    const buildPayload = useCallback((session, closedAt = new Date()) => {
        if (!session) return null
        const durationSeconds = Math.min(
            MAX_VIEW_SECONDS,
            Math.max(0, Math.round((closedAt.getTime() - session.startedAt.getTime()) / 1000)),
        )
        if (durationSeconds < MIN_VIEW_SECONDS) return null

        return {
            p_card_id: session.cardId,
            p_duration_seconds: durationSeconds,
            p_zone: session.zone,
            p_location_label: session.label,
            p_opened_at: session.startedAt.toISOString(),
            p_closed_at: closedAt.toISOString(),
        }
    }, [])

    const persistSession = useCallback((session, { keepalive = false } = {}) => {
        if (persistedRef.current) return
        const payload = buildPayload(session)
        if (!payload) return
        persistedRef.current = true

        if (keepalive) {
            const body = JSON.stringify(payload)
            void fetch('/rpc/record_card_view_session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body,
                keepalive: true,
            })
            return
        }

        void kanbify.rpc('record_card_view_session', payload)
    }, [buildPayload])

    useEffect(() => {
        if (!cardId) {
            setElapsedSeconds(0)
            return undefined
        }

        persistedRef.current = false
        const startedAt = new Date()
        const rememberedSeconds = readRememberedElapsed(cardId)
        sessionRef.current = {
            cardId,
            startedAt,
            rememberedSeconds,
            zone: viewContext?.zone || null,
            label: viewContext?.label || null,
        }
        setElapsedSeconds(rememberedSeconds)

        const intervalId = window.setInterval(() => {
            const session = sessionRef.current
            if (!session) return
            const segmentSeconds = Math.max(0, Math.round((Date.now() - session.startedAt.getTime()) / 1000))
            setElapsedSeconds(Math.min(
                MAX_VIEW_SECONDS,
                session.rememberedSeconds + segmentSeconds,
            ))
        }, 1000)

        const handlePageHide = () => {
            const session = sessionRef.current
            if (!session) return
            const segmentSeconds = Math.max(0, Math.round((Date.now() - session.startedAt.getTime()) / 1000))
            rememberElapsed(session.cardId, session.rememberedSeconds + segmentSeconds)
            persistSession(session, { keepalive: true })
        }
        window.addEventListener('pagehide', handlePageHide)

        return () => {
            const session = sessionRef.current
            sessionRef.current = null
            window.clearInterval(intervalId)
            window.removeEventListener('pagehide', handlePageHide)
            if (!session || session.cardId !== cardId) return
            const segmentSeconds = Math.max(0, Math.round((Date.now() - session.startedAt.getTime()) / 1000))
            rememberElapsed(cardId, session.rememberedSeconds + segmentSeconds)
            persistSession(session)
        }
    }, [cardId, persistSession, viewContext?.label, viewContext?.zone])

    return {
        elapsedSeconds,
        label: viewContext?.label || null,
        zone: viewContext?.zone || null,
        active: Boolean(cardId),
    }
}
